import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { IconBase, type IconBaseProps } from "./icon-base";
export interface IconPaths {
  outlined: string;
  filled: string;
}
export interface IconConfig {
  paths: IconPaths;
  /**
   * Custom viewBox for this icon. Defaults to "0 0 24 24"
   */
  viewBox?: string;
}
export interface IconComponentProps extends Omit<IconBaseProps, "children"> {
  variant?: "outlined" | "filled";
}

/**
 * Type for icon components created by the factory
 */
export type IconComponent = React.ForwardRefExoticComponent<IconComponentProps & React.RefAttributes<SVGSVGElement>>;

/**
 * Factory function to create icon components with minimal boilerplate.
 *
 * @param displayName - The display name for the component (used in React DevTools)
 * @param config - Object containing paths and optional viewBox
 * @returns A forwardRef icon component
 *
 * @example
 * export const ArrowRight = createIcon("ArrowRight", {
 *   paths: { outlined: "M647-440H160v-80h487...", filled: "M647-440H160v-80h487..." },
 *   viewBox: "0 0 24 24"
 * });
 */
export function createIcon(displayName: string, config: IconConfig | IconPaths) {
  // Support both old format (IconPaths) and new format (IconConfig)
  const {
    paths,
    viewBox
  } = "paths" in config ? config : {
    paths: config,
    viewBox: undefined
  };
  const Icon = React.forwardRef<SVGSVGElement, IconComponentProps>((t0, ref) => {
    const $ = _c(9);
    let props;
    let t1;
    if ($[0] !== t0) {
      ({
        variant: t1,
        ...props
      } = t0);
      $[0] = t0;
      $[1] = props;
      $[2] = t1;
    } else {
      props = $[1];
      t1 = $[2];
    }
    const variant = t1 === undefined ? "outlined" : t1;
    const t2 = paths[variant];
    let t3;
    if ($[3] !== t2) {
      t3 = <path d={t2} />;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== props || $[6] !== ref || $[7] !== t3) {
      t4 = <IconBase ref={ref} viewBox={viewBox} {...props}>{t3}</IconBase>;
      $[5] = props;
      $[6] = ref;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  });
  Icon.displayName = displayName;
  return Icon;
}